import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, GripVertical, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { CompetencyBadge } from './competency-badge';
import type { WizardQuestion } from './interviews.types';
import { ScoringExamplesPanel } from './scoring-examples-panel';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Checkbox } from '~/components/ui/checkbox';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';

type QuestionCardProps = {
  id: string;
  question: WizardQuestion;
  defaultEditing?: boolean;
  onUpdate: (updated: WizardQuestion) => void;
  onDelete: () => void;
  onRegenerateScoring?: (
    questionText: string
  ) => Promise<{ competencyName: string; score1: string; score3: string; score5: string } | null>;
};

export function QuestionCard({
  id,
  question,
  defaultEditing,
  onUpdate,
  onDelete,
  onRegenerateScoring
}: QuestionCardProps): React.ReactNode {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(defaultEditing ?? false);
  const [editText, setEditText] = useState(question.text);
  const [regenerateScoring, setRegenerateScoring] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(false);
  const scoringPanelOpenRef = useRef(false);
  const lastSubmittedTextRef = useRef(question.text);

  // Keep a stable ref to onUpdate so async callbacks always call the latest version
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  });

  const showRegenerateCheckbox = question.category === 'assessment' && onRegenerateScoring != null;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  async function saveEdit(): Promise<void> {
    const trimmed = editText.trim();
    if (!trimmed) {
      onDelete();
      return;
    }

    // Capture stable values before any async operations
    const baseQuestion = question;
    const regenFn =
      regenerateScoring && baseQuestion.category === 'assessment' ? onRegenerateScoring : null;

    setEditing(false);

    // Immediately reflect the text change in the parent
    onUpdateRef.current({ ...baseQuestion, text: trimmed, aiGenerated: false });
    lastSubmittedTextRef.current = trimmed;

    if (regenFn) {
      setIsRegenerating(true);
      setRegenerateError(false);
      try {
        const result = await regenFn(trimmed);
        if (result) {
          onUpdateRef.current({
            ...baseQuestion,
            text: trimmed,
            aiGenerated: false,
            competencyName: result.competencyName,
            scoringExamples: { score1: result.score1, score3: result.score3, score5: result.score5 }
          });
          setRegenerateError(false);
        } else {
          setRegenerateError(true);
          toast.error(t('wizard.questions.regenerateScoringError'));
        }
      } finally {
        setIsRegenerating(false);
      }
    }
  }

  function cancelEdit(): void {
    if (!question.text.trim()) {
      onDelete();
      return;
    }
    setEditText(question.text);
    setEditing(false);
  }

  async function retryRegenerate(): Promise<void> {
    if (!onRegenerateScoring || question.category !== 'assessment') return;
    setIsRegenerating(true);
    setRegenerateError(false);
    try {
      const result = await onRegenerateScoring(lastSubmittedTextRef.current);
      if (result) {
        onUpdateRef.current({
          ...question,
          competencyName: result.competencyName,
          scoringExamples: { score1: result.score1, score3: result.score3, score5: result.score5 }
        });
      } else {
        setRegenerateError(true);
        toast.error(t('wizard.questions.regenerateScoringError'));
      }
    } finally {
      setIsRegenerating(false);
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-start gap-2 border-b border-border py-2 last:border-b-0 sm:gap-3 sm:rounded-md sm:border sm:bg-card sm:p-3 sm:shadow-sm"
    >
      <button
        type="button"
        className="mt-0.5 hidden shrink-0 cursor-grab touch-none opacity-0 group-hover:opacity-100 sm:flex"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4 text-muted-foreground" />
      </button>

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex flex-col gap-2">
            <Textarea
              value={editText}
              onChange={(e) => {
                setEditText(e.target.value);
              }}
              className="min-h-[60px] text-sm"
              autoFocus
            />
            {showRegenerateCheckbox && (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={regenerateScoring} onCheckedChange={setRegenerateScoring} />
                {regenerateScoring
                  ? t('wizard.questions.regenerateScoringExamples')
                  : t('wizard.questions.dontRegenerateScoringExamples')}
              </label>
            )}
          </div>
        ) : (
          <p className="text-sm text-foreground">{question.text}</p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {question.aiGenerated && (
            <span className="text-xs text-muted-foreground">
              {t('wizard.questions.aiGenerated')}
            </span>
          )}
          {question.isOptional && (
            <Badge variant="outline" className="text-xs">
              {t('wizard.questions.optional')}
            </Badge>
          )}
          {isRegenerating ? (
            <Skeleton className="h-5 w-28 rounded-full" />
          ) : (
            question.competencyName && <CompetencyBadge name={question.competencyName} />
          )}
        </div>

        {isRegenerating && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            {t('wizard.questions.regeneratingScoringExamples')}
          </div>
        )}

        {isRegenerating && scoringPanelOpenRef.current ? (
          <div className="mt-2 flex flex-col gap-1.5 sm:grid sm:grid-cols-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : (
          !isRegenerating &&
          question.scoringExamples && (
            <ScoringExamplesPanel
              score1={question.scoringExamples.score1}
              score3={question.scoringExamples.score3}
              score5={question.scoringExamples.score5}
              defaultOpen={scoringPanelOpenRef.current}
              onOpenChange={(open) => {
                scoringPanelOpenRef.current = open;
              }}
              onUpdate={(examples) => {
                onUpdate({ ...question, scoringExamples: examples });
              }}
            />
          )
        )}

        {regenerateError && !isRegenerating && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-destructive">
              {t('wizard.questions.regenerateScoringError')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-0.5 text-xs"
              onClick={() => void retryRegenerate()}
            >
              {t('wizard.questions.retry')}
            </Button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="flex shrink-0 flex-col gap-1 sm:flex-row">
          <Button variant="ghost" size="icon-sm" onClick={() => void saveEdit()}>
            <Check className="size-4 text-primary" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={cancelEdit}>
            <X className="size-4 text-muted-foreground" />
          </Button>
        </div>
      ) : (
        <div className="flex shrink-0 flex-col gap-1 opacity-0 group-hover:opacity-100 sm:flex-row">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              setEditText(question.text);
              setRegenerateScoring(true);
              setEditing(true);
            }}
          >
            <Pencil className="size-4 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon-sm" onClick={onDelete}>
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      )}
    </div>
  );
}
